import { Alert, Box, Button, Divider, Input, Sheet } from "@mui/joy";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { accountKitApi } from "@nerdjs/account-kit";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formOpenSelector, setFormOpen } from "../../reducers/rhapsody";
import { useCurrentUser } from "../../redux/user/userHook";
import {
  useUpdateCurrentUserMutation,
  useValidateVerificationCodeMutation,
} from "../../redux/verificationCode/verificationCodeHooks";
import DialogClose from "../dialogClose/dialogClose";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

/**
 *
 * @returns {ReactElement} AccountCredentials page
 */
export function AccountCredentials() {
  const open = useSelector(formOpenSelector("accountCredentials"));
  const dispatch = useDispatch();
  const { me: data } = useCurrentUser();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [validateCode] = useValidateVerificationCodeMutation();

  const [email, setEmail] = useState(data?.email ?? "");
  const [phone, setPhone] = useState(data?.phone ?? "");
  const [sentTo, setSentTo] = useState("");
  const [kind, setKind] = useState("");
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setEmail(data?.email ?? "");
    setPhone(data?.phone ?? "");
  }, [data]);

  const onClose = () => {
    setError("");
    setKind("");
    setSentTo("");
    setAskCode(false);
    setCode("");
    dispatch(setFormOpen({ formID: "accountCredentials", isOpen: false }));
  };

  return (
    <>
      <DialogClose
        open={open}
        onClose={onClose}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>
          <DialogHeader
            title="Account Credentials"
            subtitle="Change your email & phone"
          />
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: 400,
          }}
        >
          {error ? <Alert color="danger">{error}</Alert> : []}
          <GroupCaption>Sign-in details</GroupCaption>
          <Sheet variant="soft" sx={groupSheetSx}>
            <Input
              sx={{ background: "transparent" }}
              variant="plain"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-envelope"
                  color={tileColors.blue}
                  label="Login Email"
                />
              }
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
            />
            {data?.email !== email ? (
              <Box sx={{ display: "flex", justifyContent: "end", p: 1 }}>
                <Button
                  size="sm"
                  sx={pillButtonSx}
                  onClick={() => {
                    setError("");
                    updateCurrentUser({ email })
                      .unwrap()
                      .then((e) => {
                        setAskCode(true);
                        setSentTo(email);
                        setKind("EMAIL");
                      })
                      .catch((e) => {
                        setError(e?.data?.description);
                      });
                  }}
                >
                  Save new email
                </Button>
              </Box>
            ) : (
              []
            )}
            <Divider />
            <Box
              sx={{
                display: "flex",
                pl: 1.5,
                pr: 1,
                alignItems: "center",
                height: 40,
                justifyContent: "space-between",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <FieldLabel
                icon="fa-solid fa-phone"
                color={tileColors.green}
                label="Phone Number"
              />
              <Box
                sx={{
                  width: 200,
                  height: 40,
                  position: "absolute",
                  top: 4,
                  right: 12,
                }}
                className="musician"
              >
                <NerdPhoneField
                  value={phone}
                  onAccept={(e) => {
                    setPhone(e);
                  }}
                  textFieldProps={{
                    fullWidth: true,
                    variant: "standard",
                    inputProps: {
                      border: "none",
                      disableUnderline: true,
                      style: { textAlign: "right" },
                    },
                    size: "small",
                    sx: { background: "transparent" },
                  }}
                />
              </Box>
            </Box>
            {data?.phone !== phone ? (
              <Box sx={{ display: "flex", justifyContent: "end", p: 1 }}>
                <Button
                  size="sm"
                  sx={pillButtonSx}
                  onClick={() => {
                    setError("");
                    updateCurrentUser({ phone })
                      .unwrap()
                      .then((e) => {
                        setAskCode(true);
                        setSentTo(phone);
                        setKind("PHONE");
                      })
                      .catch((e) => {
                        setError(e?.data?.description);
                      });
                  }}
                >
                  Save new phone
                </Button>
              </Box>
            ) : (
              []
            )}
          </Sheet>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            variant="plain"
            color="neutral"
            sx={pillButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </DialogClose>
      <DialogClose
        open={askCode}
        onClose={() => setAskCode(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>
          <DialogHeader
            title="Enter the 6 digits code:"
            subtitle={`Sent to ${sentTo}`}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: 360,
          }}
        >
          <Sheet variant="soft" sx={groupSheetSx}>
            <Input
              sx={{ background: "transparent" }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-shield-halved"
                  color={tileColors.indigo}
                  label="Code"
                />
              }
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              placeholder="6 digits code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
          </Sheet>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAskCode(false);
              setPhone(data.phone);
              setEmail(data.email);
            }}
            color="neutral"
            variant="plain"
            sx={pillButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setAskCode(false);
              setPhone(data.phone);
              setEmail(data.email);
              validateCode({ kind, value: sentTo })
                .unwrap()
                .then(() => {
                  dispatch(accountKitApi.util.invalidateTags(["user"]));
                })
                .catch((e) => {
                  setError(e?.data?.description);
                });
            }}
            variant="solid"
            sx={pillButtonSx}
          >
            Verify
          </Button>
        </DialogActions>
      </DialogClose>
    </>
  );
}
